import React from 'react';
import SeoMeta from '../components/SeoMeta';

const TerminosPage = () => {
  return (
    <>
      <SeoMeta
        title="Condiciones Generales de Uso - BlueCMR"
        description="Condiciones Generales de Uso de la plataforma BlueCMR para documentos de transporte digitales."
        canonical="https://www.bluecmr.com/terminos"
      />

      <div className="bg-gray-50 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-sm p-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-6">
              Condiciones Generales de Uso de BlueCMR
            </h1>

            <p className="text-sm text-gray-600 mb-8">
              Última actualización: 22 de abril de 2026
            </p>

            {/* 1. Identificación del prestador */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                1. Identificación del prestador
              </h2>
              <p className="text-gray-700 mb-4">
                BlueCMR es una plataforma tecnológica operada por <strong>BlueCMR S.L.U.</strong>, con NIF <strong>B26654517</strong> y domicilio social en <strong>Calle Fueros de León 1, 24402, Ponferrada, León, España</strong> (en adelante, "BlueCMR").
              </p>
              <p className="text-gray-700">
                Para cualquier consulta relacionada con estas Condiciones, puede contactarse con BlueCMR en: <strong>support@bluecmr.com</strong>.
              </p>
            </section>

            {/* 2. Definiciones */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                2. Definiciones
              </h2>
              <p className="text-gray-700 mb-2">A efectos de estas Condiciones:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li><strong>Plataforma:</strong> la plataforma BlueCMR, accesible a través de web, aplicación móvil o cualquier otra interfaz habilitada por BlueCMR.</li>
                <li><strong>Cliente:</strong> la persona jurídica o profesional que contrata o utiliza BlueCMR para sus actividades empresariales o profesionales.</li>
                <li><strong>Usuario Autorizado:</strong> cualquier persona física autorizada por el Cliente para acceder o utilizar la Plataforma por cuenta de este.</li>
                <li><strong>Servicios:</strong> los servicios prestados por BlueCMR a través de la Plataforma, incluyendo, entre otros, la gestión digital de documentos de transporte, evidencias, firma electrónica y funcionalidades asociadas.</li>
                <li><strong>Contenido del Cliente:</strong> los datos, documentos, imágenes, firmas, evidencias y demás información cargada, generada, transmitida o gestionada por el Cliente o sus Usuarios Autorizados a través de la Plataforma.</li>
              </ul>
            </section>

            {/* 3. Objeto y ámbito de aplicación */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                3. Objeto y ámbito de aplicación
              </h2>
              <p className="text-gray-700 mb-4">
                Las presentes Condiciones regulan el acceso, registro, navegación y uso de la Plataforma y de los Servicios ofrecidos por BlueCMR.
              </p>
              <p className="text-gray-700 mb-4">
                Estas Condiciones se aplican a cualquier modalidad de uso de BlueCMR, salvo en aquello que quede expresamente regulado por un contrato, pedido, propuesta aceptada, acuerdo de suscripción, anexo económico o condiciones particulares firmadas entre BlueCMR y el Cliente. En caso de contradicción, prevalecerán dichos documentos particulares sobre estas Condiciones.
              </p>
              <p className="text-gray-700">
                El uso de BlueCMR está dirigido principalmente a clientes empresariales o profesionales. Cuando un Usuario Autorizado actúe en nombre de una organización, declara y garantiza que cuenta con capacidad suficiente para vincular a dicha organización en la medida aplicable.
              </p>
            </section>

            {/* 4. Aceptación de las Condiciones */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                4. Aceptación de las Condiciones
              </h2>
              <p className="text-gray-700 mb-4">
                El acceso o uso de la Plataforma requiere la aceptación de estas Condiciones, así como de cualesquiera otros documentos contractuales o políticas que resulten aplicables, incluyendo, en su caso:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li>la Política de Privacidad;</li>
                <li>el Data Processing Agreement o DPA, cuando BlueCMR trate datos personales por cuenta del Cliente;</li>
                <li>las condiciones particulares o comerciales aplicables al servicio contratado.</li>
              </ul>
              <p className="text-gray-700 mb-4">
                La aceptación podrá formalizarse mediante firma, clickwrap, marcación de casilla, aceptación electrónica, uso continuado de la Plataforma o cualquier otro mecanismo válido de manifestación del consentimiento.
              </p>
              <p className="text-gray-700">
                Si el Usuario Autorizado no acepta estas Condiciones, no deberá utilizar la Plataforma.
              </p>
            </section>

            {/* 5. Registro, cuentas y acceso */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                5. Registro, cuentas y acceso
              </h2>
              <p className="text-gray-700 mb-4">
                Para acceder a determinadas funcionalidades, BlueCMR podrá exigir el registro previo de una cuenta.
              </p>
              <p className="text-gray-700 mb-2">El Cliente y sus Usuarios Autorizados se obligan a:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li>proporcionar información veraz, exacta y actualizada;</li>
                <li>mantener actualizados los datos de la cuenta;</li>
                <li>custodiar diligentemente sus credenciales;</li>
                <li>no compartir accesos de forma no autorizada;</li>
                <li>notificar sin demora cualquier uso no autorizado o incidente de seguridad relacionado con la cuenta.</li>
              </ul>
              <p className="text-gray-700 mb-4">
                El Cliente será responsable de la administración de sus Usuarios Autorizados y del uso de la Plataforma realizado desde sus cuentas, sin perjuicio de la responsabilidad individual que pudiera corresponder a cada Usuario Autorizado.
              </p>
              <p className="text-gray-700">
                BlueCMR podrá suspender o cancelar cuentas cuando detecte indicios razonables de uso ilícito, fraudulento, inseguro, contrario a estas Condiciones o contrario a la normativa aplicable.
              </p>
            </section>

            {/* 6. Uso permitido y restricciones */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                6. Uso permitido y restricciones
              </h2>
              <p className="text-gray-700 mb-4">
                El Cliente y sus Usuarios Autorizados se comprometen a utilizar BlueCMR de conformidad con la ley, la buena fe, el orden público y estas Condiciones.
              </p>
              <p className="text-gray-700 mb-2">Queda prohibido, entre otros supuestos:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li>utilizar la Plataforma para fines ilícitos, fraudulentos o no autorizados;</li>
                <li>introducir datos falsos o engañosos;</li>
                <li>revender, sublicenciar, explotar comercialmente o poner a disposición de terceros la Plataforma sin autorización expresa de BlueCMR;</li>
                <li>intentar acceder sin autorización a sistemas, cuentas, entornos o datos de terceros;</li>
                <li>realizar ingeniería inversa, descompilar, desensamblar o intentar extraer el código fuente de la Plataforma, salvo en los casos legalmente permitidos;</li>
                <li>introducir malware, virus o cualquier elemento destinado a alterar, dañar o comprometer la seguridad o disponibilidad del servicio;</li>
                <li>interferir o sobrecargar de forma deliberada la infraestructura de BlueCMR;</li>
                <li>utilizar la Plataforma de forma que infrinja derechos de terceros, incluyendo propiedad intelectual, secretos empresariales o normativa de protección de datos.</li>
              </ul>
              <p className="text-gray-700">
                El Cliente será responsable del uso de BlueCMR por parte de sus Usuarios Autorizados, así como de asegurarse de que dicho uso se ajusta a sus políticas internas y a la normativa aplicable.
              </p>
            </section>

            {/* 7. Contenido del Cliente */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                7. Contenido del Cliente
              </h2>
              <p className="text-gray-700 mb-4">
                El Cliente conserva la titularidad de su Contenido del Cliente.
              </p>
              <p className="text-gray-700 mb-2">El Cliente declara y garantiza que:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li>dispone de todos los derechos, títulos, autorizaciones y bases legales necesarios sobre el Contenido del Cliente;</li>
                <li>dicho contenido es lícito y no infringe derechos de terceros;</li>
                <li>los datos personales incluidos en dicho contenido han sido tratados e incorporados a BlueCMR de forma conforme a la normativa aplicable.</li>
              </ul>
              <p className="text-gray-700 mb-4">
                BlueCMR no adquiere derecho de propiedad alguno sobre el Contenido del Cliente. No obstante, el Cliente concede a BlueCMR una licencia no exclusiva, limitada y gratuita durante la vigencia de la relación contractual para alojar, reproducir, tratar, comunicar y poner a disposición dicho contenido en la medida necesaria para prestar los Servicios.
              </p>
              <p className="text-gray-700">
                BlueCMR no supervisa de forma proactiva todo el Contenido del Cliente, pero podrá retirar, bloquear o restringir el acceso a contenidos cuando tenga indicios razonables de ilicitud, riesgo de seguridad o infracción de estas Condiciones.
              </p>
            </section>

            {/* 8. Firma electrónica y evidencias */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                8. Firma electrónica y evidencias
              </h2>
              <p className="text-gray-700 mb-4">
                BlueCMR puede poner a disposición del Cliente distintas funcionalidades de firma electrónica y de generación de evidencias, incluyendo, entre otras, firma manuscrita digitalizada, OTP, firma biométrica, firma avanzada, firma delegada, firma automática, sello electrónico y otras modalidades técnicamente disponibles.
              </p>
              <p className="text-gray-700 mb-2">El Cliente reconoce y acepta expresamente que:</p>
              <ol className="list-[lower-alpha] pl-6 space-y-3 text-gray-700 mb-4">
                <li>toda firma realizada a través de la Plataforma se efectúa por cuenta y bajo la responsabilidad del Cliente o de la entidad a la que represente el Usuario Autorizado correspondiente;</li>
                <li>corresponde exclusivamente al Cliente verificar la identidad, legitimación, capacidad, representación, poderes y autorizaciones internas de las personas que firman o intervienen en documentos gestionados a través de la Plataforma;</li>
                <li>BlueCMR actúa exclusivamente como proveedor tecnológico y no valida por sí misma la capacidad jurídica, poderes de representación o legitimación material de los firmantes;</li>
                <li>la utilización de funcionalidades de firma delegada, automática o mediante sello electrónico requiere que el Cliente disponga de las autorizaciones necesarias y asuma la plena responsabilidad por su activación y uso;</li>
                <li>BlueCMR no asume la condición de firmante, representante, mandataria, parte contractual ni interviniente material en el documento firmado, aunque aporte medios técnicos, evidencias o mecanismos de sellado;</li>
                <li>la validez, eficacia, oponibilidad o reconocimiento de un documento firmado dependerá también del uso correcto de la Plataforma, de la configuración elegida, del contexto normativo aplicable y de las relaciones entre las partes intervinientes.</li>
              </ol>
              <p className="text-gray-700">
                Cualquier controversia relativa a la legitimación del firmante, a la validez interna del documento, a la autorización para firmar o a los efectos jurídicos del mismo será responsabilidad del Cliente, salvo que derive directamente de un incumplimiento imputable a BlueCMR en la prestación del Servicio.
              </p>
            </section>

            {/* 9. Naturaleza del servicio */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                9. Naturaleza del servicio
              </h2>
              <p className="text-gray-700 mb-4">
                BlueCMR es una plataforma tecnológica y documental. BlueCMR no actúa como transportista, operador logístico, cargador, destinatario, expedidor ni representante material de las partes que intervienen en una operación de transporte, salvo que se pacte expresamente lo contrario por escrito.
              </p>
              <p className="text-gray-700 mb-4">
                La Plataforma facilita la gestión, creación, intercambio, almacenamiento, consulta y trazabilidad de documentos y evidencias electrónicas, pero no sustituye por sí misma las obligaciones legales o contractuales propias del Cliente o de terceros intervinientes en la operación.
              </p>
              <p className="text-gray-700 mb-2">El Cliente seguirá siendo el único responsable de:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>la corrección, integridad y licitud de los datos y documentos introducidos;</li>
                <li>el cumplimiento de las obligaciones legales y regulatorias aplicables a su actividad;</li>
                <li>las relaciones materiales con terceros intervinientes en la cadena logística;</li>
                <li>verificar si el uso de documentos electrónicos resulta suficiente o adecuado en cada caso concreto.</li>
              </ul>
            </section>

            {/* 10. Disponibilidad, soporte y cambios del servicio */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                10. Disponibilidad, soporte y cambios del servicio
              </h2>
              <p className="text-gray-700 mb-4">
                BlueCMR realizará esfuerzos razonables para mantener la disponibilidad y funcionamiento regular de la Plataforma, sin garantizar una disponibilidad ininterrumpida ni libre de errores.
              </p>
              <p className="text-gray-700 mb-2">El Servicio podrá verse afectado por:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li>mantenimientos programados;</li>
                <li>actualizaciones;</li>
                <li>incidencias técnicas;</li>
                <li>caídas de terceros proveedores;</li>
                <li>causas de fuerza mayor;</li>
                <li>o cualquier otra circunstancia razonablemente ajena al control de BlueCMR.</li>
              </ul>
              <p className="text-gray-700 mb-4">
                BlueCMR podrá modificar, actualizar, sustituir o discontinuar funcionalidades de la Plataforma por razones técnicas, operativas, legales, regulatorias o de negocio. Cuando dichos cambios afecten de forma material al Servicio contratado, BlueCMR lo comunicará por medios razonables con antelación suficiente.
              </p>
              <p className="text-gray-700">
                Salvo pacto expreso en contrario, BlueCMR no asume obligaciones de nivel de servicio o SLA específicas más allá de las derivadas de la diligencia profesional razonable en la prestación del Servicio.
              </p>
            </section>

            {/* 11. Propiedad intelectual e industrial */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                11. Propiedad intelectual e industrial
              </h2>
              <p className="text-gray-700 mb-4">
                Todos los derechos de propiedad intelectual e industrial sobre la Plataforma, su software, diseño, estructura, interfaces, documentación, marcas, nombres comerciales, logotipos, bases de datos, funcionalidades, desarrollos, know-how y demás elementos del Servicio corresponden a BlueCMR o a sus licenciantes.
              </p>
              <p className="text-gray-700 mb-4">
                El uso de la Plataforma no implica cesión alguna de derechos a favor del Cliente o de los Usuarios Autorizados.
              </p>
              <p className="text-gray-700">
                BlueCMR concede al Cliente, durante la vigencia de la relación contractual, una licencia limitada, no exclusiva, revocable, no sublicenciable e intransferible para acceder y utilizar la Plataforma conforme a estas Condiciones y, en su caso, a los documentos contractuales aplicables.
              </p>
            </section>

            {/* 12. Protección de datos personales */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                12. Protección de datos personales
              </h2>
              <p className="text-gray-700 mb-4">
                El tratamiento de datos personales por parte de BlueCMR se regirá por la normativa aplicable y por la documentación contractual correspondiente.
              </p>
              <p className="text-gray-700 mb-4">
                Cuando BlueCMR trate datos personales por cuenta del Cliente en el marco de la prestación de los Servicios, actuará como encargado del tratamiento en los términos previstos en el DPA aplicable.
              </p>
              <p className="text-gray-700 mb-4">
                La información sobre el tratamiento de datos personales relativos a contactos, usuarios de cuenta, representantes o visitantes del sitio web se regula en la Política de Privacidad de BlueCMR.
              </p>
              <p className="text-gray-700">
                El Cliente garantiza que dispone de base jurídica suficiente para el tratamiento y comunicación a BlueCMR de los datos personales incorporados a la Plataforma.
              </p>
            </section>

            {/* 13. Confidencialidad */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                13. Confidencialidad
              </h2>
              <p className="text-gray-700 mb-4">
                Cada parte se compromete a mantener confidencial la información no pública de la otra parte a la que acceda con ocasión de la relación contractual y a utilizarla únicamente para la ejecución de la misma.
              </p>
              <p className="text-gray-700 mb-2">No tendrá la consideración de información confidencial aquella que:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li>sea o pase a ser pública sin infracción de esta obligación;</li>
                <li>ya estuviera legítimamente en posesión de la parte receptora;</li>
                <li>deba revelarse por obligación legal, judicial o administrativa;</li>
                <li>haya sido obtenida legítimamente de un tercero sin deber de confidencialidad.</li>
              </ul>
              <p className="text-gray-700">
                Esta obligación permanecerá vigente durante la relación contractual y durante cinco (5) años tras su terminación, salvo que por su naturaleza deba mantenerse durante más tiempo.
              </p>
            </section>

            {/* 14. Condiciones económicas */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                14. Condiciones económicas
              </h2>
              <p className="text-gray-700 mb-4">
                Los Servicios de BlueCMR podrán ser gratuitos o de pago.
              </p>
              <p className="text-gray-700 mb-4">
                Cuando el Servicio sea de pago, las condiciones económicas aplicables serán las establecidas en la propuesta comercial aceptada, acuerdo de suscripción, pedido, anexo económico o documentación contractual equivalente.
              </p>
              <p className="text-gray-700 mb-2">Salvo que se pacte expresamente otra cosa por escrito:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li>los importes se entienden sin impuestos indirectos;</li>
                <li>el Cliente deberá pagar las cantidades facturadas en los plazos indicados en la factura o contrato aplicable;</li>
                <li>los retrasos en el pago podrán devengar intereses y dar lugar a suspensión del Servicio previa notificación razonable.</li>
              </ul>
              <p className="text-gray-700">
                BlueCMR podrá actualizar sus tarifas para nuevas contrataciones, renovaciones o periodos sucesivos, o bien durante la vigencia de la relación cuando así lo permitan expresamente los documentos contractuales aplicables y se comunique con antelación razonable.
              </p>
            </section>

            {/* 15. Suspensión */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                15. Suspensión
              </h2>
              <p className="text-gray-700 mb-2">BlueCMR podrá suspender total o parcialmente el acceso a la Plataforma cuando:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li>exista impago;</li>
                <li>concurra un riesgo razonable para la seguridad, integridad o disponibilidad del Servicio;</li>
                <li>detecte uso ilícito, fraudulento o contrario a estas Condiciones;</li>
                <li>exista requerimiento legal o de autoridad competente;</li>
                <li>sea necesario para realizar mantenimiento urgente o mitigar un incidente.</li>
              </ul>
              <p className="text-gray-700">
                Siempre que resulte razonablemente posible, BlueCMR informará previamente al Cliente de la suspensión y de sus causas.
              </p>
            </section>

            {/* 16. Exclusión de garantías */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                16. Exclusión de garantías
              </h2>
              <p className="text-gray-700 mb-4">
                En la máxima medida permitida por la ley, la Plataforma se ofrece "tal cual" y "según disponibilidad".
              </p>
              <p className="text-gray-700 mb-2">BlueCMR no otorga garantías expresas o implícitas sobre:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li>disponibilidad ininterrumpida;</li>
                <li>ausencia total de errores;</li>
                <li>adecuación a un propósito particular no pactado expresamente;</li>
                <li>cumplimiento automático de requisitos normativos específicos del Cliente;</li>
                <li>reconocimiento universal de documentos o firmas por terceros;</li>
                <li>o resultados concretos derivados del uso de la Plataforma.</li>
              </ul>
              <p className="text-gray-700">
                BlueCMR no garantiza por sí misma que cualquier documento generado o firmado a través de la Plataforma sea automáticamente válido, eficaz o aceptado en cualquier jurisdicción, procedimiento, autoridad o relación con terceros. Corresponde al Cliente verificar la adecuación de su uso al marco normativo y operativo aplicable.
              </p>
            </section>

            {/* 17. Limitación de responsabilidad */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                17. Limitación de responsabilidad
              </h2>
              <p className="text-gray-700 mb-4">
                En la máxima medida permitida por la ley, BlueCMR no será responsable por daños indirectos, especiales, incidentales, punitivos o consecuenciales, ni por lucro cesante, pérdida de ingresos, pérdida de negocio, pérdida de reputación, pérdida de datos o interrupción de actividad.
              </p>
              <p className="text-gray-700 mb-4">
                La responsabilidad total agregada de BlueCMR derivada de o relacionada con la Plataforma o los Servicios quedará limitada al importe efectivamente abonado por el Cliente a BlueCMR en los doce (12) meses anteriores al hecho causante de la reclamación.
              </p>
              <p className="text-gray-700 mb-4">
                Las exclusiones y limitaciones anteriores no se aplicarán en aquellos supuestos en los que la responsabilidad no pueda excluirse o limitarse válidamente por ley, ni en casos de dolo o culpa grave cuando la normativa aplicable impida dicha limitación.
              </p>
              <p className="text-gray-700 mb-2">BlueCMR no será responsable de:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>la relación material entre las partes intervinientes en la operación de transporte;</li>
                <li>el contenido introducido por el Cliente;</li>
                <li>la legitimación o poderes de los firmantes;</li>
                <li>el incumplimiento por el Cliente de sus obligaciones legales o contractuales;</li>
                <li>incidencias imputables a terceros proveedores, redes de telecomunicaciones o sistemas del Cliente, fuera del control razonable de BlueCMR.</li>
              </ul>
            </section>

            {/* 18. Indemnidad */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                18. Indemnidad
              </h2>
              <p className="text-gray-700 mb-2">
                El Cliente deberá mantener indemne a BlueCMR, sus administradores, empleados y colaboradores frente a reclamaciones, acciones, sanciones, daños, pérdidas, costes y gastos razonables derivados de:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li>el incumplimiento por el Cliente o sus Usuarios Autorizados de estas Condiciones o de la normativa aplicable;</li>
                <li>el Contenido del Cliente;</li>
                <li>la falta de legitimación o autorización de firmantes;</li>
                <li>el uso ilícito o no autorizado de la Plataforma;</li>
                <li>reclamaciones de terceros relacionadas con documentos, datos, instrucciones o actuaciones imputables al Cliente.</li>
              </ul>
              <p className="text-gray-700">
                Lo anterior no resultará aplicable en la medida en que la reclamación derive directamente de un incumplimiento imputable a BlueCMR.
              </p>
            </section>

            {/* 19. Duración y terminación */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                19. Duración y terminación
              </h2>
              <p className="text-gray-700 mb-4">
                Estas Condiciones permanecerán vigentes mientras el Cliente o sus Usuarios Autorizados utilicen la Plataforma o mantengan una relación contractual activa con BlueCMR.
              </p>
              <p className="text-gray-700 mb-4">
                El Cliente podrá dejar de utilizar la Plataforma y solicitar la baja de su cuenta conforme a los procedimientos habilitados por BlueCMR, sin perjuicio de las obligaciones económicas pendientes y de las responsabilidades derivadas de actuaciones previas.
              </p>
              <p className="text-gray-700 mb-2">BlueCMR podrá resolver o terminar la relación contractual o el acceso a la Plataforma en caso de:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li>incumplimiento grave de estas Condiciones;</li>
                <li>impago persistente;</li>
                <li>uso fraudulento, ilícito o inseguro;</li>
                <li>requerimiento legal;</li>
                <li>discontinuación del Servicio por decisión empresarial, en cuyo caso lo comunicará con antelación razonable.</li>
              </ul>
              <p className="text-gray-700">
                A la terminación, seguirán vigentes aquellas disposiciones que, por su naturaleza, deban subsistir, incluyendo propiedad intelectual, confidencialidad, limitación de responsabilidad, indemnidad, protección de datos, pagos pendientes y jurisdicción.
              </p>
            </section>

            {/* 20. Exportación y conservación de datos */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                20. Exportación y conservación de datos
              </h2>
              <p className="text-gray-700 mb-4">
                Durante la vigencia del Servicio y durante el plazo razonable que, en su caso, se indique contractualmente o por BlueCMR tras la terminación, el Cliente podrá solicitar la exportación o recuperación razonable de su información en formatos estándar disponibles.
              </p>
              <p className="text-gray-700">
                BlueCMR podrá suprimir o anonimizar datos del Cliente una vez finalizada la relación, salvo obligación legal de conservación, necesidad de mantener copias de seguridad residuales o conservación justificada de evidencias, logs o registros necesarios para cumplimiento normativo, defensa de reclamaciones o seguridad del servicio.
              </p>
            </section>

            {/* 21. Modificación de las Condiciones */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                21. Modificación de las Condiciones
              </h2>
              <p className="text-gray-700 mb-4">
                BlueCMR podrá modificar estas Condiciones por motivos legales, regulatorios, técnicos, operativos o comerciales.
              </p>
              <p className="text-gray-700 mb-4">
                Cuando los cambios sean relevantes, BlueCMR lo notificará por medios razonables, incluyendo la Plataforma o el correo electrónico asociado a la cuenta. Salvo indicación distinta, las modificaciones entrarán en vigor en la fecha indicada en la propia actualización.
              </p>
              <p className="text-gray-700">
                El uso continuado de la Plataforma tras la entrada en vigor de la nueva versión supondrá su aceptación, sin perjuicio del derecho del Cliente a dejar de utilizar el Servicio si no estuviera de acuerdo con la modificación.
              </p>
            </section>

            {/* 22. Comunicaciones */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                22. Comunicaciones
              </h2>
              <p className="text-gray-700 mb-4">
                Las comunicaciones entre BlueCMR y el Cliente podrán realizarse por medios electrónicos, incluyendo correo electrónico, avisos dentro de la Plataforma o notificaciones en la cuenta correspondiente.
              </p>
              <p className="text-gray-700 mb-4">
                El Cliente reconoce la validez contractual de dichas comunicaciones electrónicas.
              </p>
              <p className="text-gray-700">
                A efectos de contacto con BlueCMR, podrá utilizarse la dirección <strong>support@bluecmr.com</strong> o cualquier otra dirección o canal que BlueCMR comunique válidamente.
              </p>
            </section>

            {/* 23. Fuerza mayor */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                23. Fuerza mayor
              </h2>
              <p className="text-gray-700">
                BlueCMR no será responsable por retrasos o incumplimientos debidos a causas fuera de su control razonable, incluyendo fallos de terceros proveedores, cortes de suministro, ataques generalizados, conflictos laborales, desastres naturales, actos de autoridad, guerra, disturbios, pandemias o incidencias de telecomunicaciones.
              </p>
            </section>

            {/* 24. Cesión */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                24. Cesión
              </h2>
              <p className="text-gray-700 mb-4">
                El Cliente no podrá ceder su posición contractual ni sus derechos u obligaciones derivados de estas Condiciones sin autorización previa y escrita de BlueCMR, salvo en supuestos de reorganización interna o transmisión universal de patrimonio que no perjudiquen materialmente a BlueCMR.
              </p>
              <p className="text-gray-700">
                BlueCMR podrá ceder o transmitir estas Condiciones o la relación contractual en el marco de una reestructuración societaria, transmisión de negocio o operación corporativa equivalente, notificándolo cuando resulte razonable.
              </p>
            </section>

            {/* 25. Nulidad parcial y no renuncia */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                25. Nulidad parcial y no renuncia
              </h2>
              <p className="text-gray-700 mb-4">
                La nulidad, invalidez o inaplicabilidad de alguna disposición de estas Condiciones no afectará a la validez del resto.
              </p>
              <p className="text-gray-700">
                La falta de ejercicio por BlueCMR de cualquier derecho o facultad no constituirá renuncia al mismo.
              </p>
            </section>

            {/* 26. Ley aplicable y jurisdicción */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                26. Ley aplicable y jurisdicción
              </h2>
              <p className="text-gray-700 mb-4">
                Estas Condiciones se regirán por la ley española.
              </p>
              <p className="text-gray-700 mb-4">
                En la medida en que la normativa aplicable lo permita, cualquier controversia derivada de estas Condiciones o del uso de la Plataforma se someterá a los Juzgados y Tribunales de León, España, con renuncia expresa a cualquier otro fuero que pudiera corresponder.
              </p>
              <p className="text-gray-700">
                Cuando resulte de aplicación normativa imperativa de protección de consumidores o reglas imperativas de jurisdicción, se estará a lo dispuesto por dicha normativa.
              </p>
            </section>

            {/* 27. Orden de prevalencia documental */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                27. Orden de prevalencia documental
              </h2>
              <p className="text-gray-700 mb-2">
                Salvo que se indique expresamente lo contrario, el orden de prevalencia entre la documentación contractual aplicable será el siguiente:
              </p>
              <ol className="list-decimal pl-6 space-y-2 text-gray-700">
                <li>acuerdo de suscripción, pedido, propuesta aceptada o condiciones particulares firmadas;</li>
                <li>anexos económicos o funcionales;</li>
                <li>DPA;</li>
                <li>presentes Condiciones;</li>
                <li>Política de Privacidad;</li>
                <li>cualquier otra política o documento auxiliar.</li>
              </ol>
            </section>

            {/* 28. Acuerdo íntegro */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                28. Acuerdo íntegro
              </h2>
              <p className="text-gray-700">
                Estas Condiciones constituyen, junto con la documentación contractual aplicable, el acuerdo íntegro entre las partes en relación con el acceso y uso de la Plataforma, y sustituyen cualesquiera acuerdos, comunicaciones o entendimientos previos sobre su objeto.
              </p>
            </section>

            {/* Footer */}
            <div className="mt-12 pt-8 border-t border-gray-200">
              <p className="text-sm text-gray-600">
                © 2026 BlueCMR S.L.U. Todos los derechos reservados.
              </p>
              <p className="text-sm text-gray-600 mt-2">
                <strong>Contacto:</strong> Para cualquier duda o consulta relacionada con estas Condiciones, puede contactarnos en <strong>support@bluecmr.com</strong>
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TerminosPage;